import type {
  ContentExportMissingTranslationsResp,
  ContentI18nStatsResp,
  ContentI18nTextSaveResp,
  ContentOwnTranslationPageResp,
  I18nL3Query,
  I18nL3RowVO,
  I18nL3VO,
  I18nLangsVO,
  I18nLangVO,
  I18nLayerRegistration,
  I18nTermListVO,
  I18nTermQuery,
  PageIndustryTermsResp,
  PlatformLanguageRowResp,
  LanguagePolicyResp,
} from '@/types';
import { gatewayRequest } from '@/utils/http';

const LANGUAGES = '/admin/platform/language/pageLanguages';
const I18N_STATS = '/admin/content/i18n/sumContentI18nStats';
const I18N_OWN = '/admin/content/i18n/pageOwnTranslations';
const defined = <T extends Record<string, unknown>>(values: T) =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  );

/**
 * 多语言页的角标统计。
 *
 * `dedupe`：`getI18nLayers()` 内部已经取过一次，页面又在同一个 `Promise.all` 里
 * 单独取一次（要 `l3Incomplete`），两条几乎同时发出、打的是同一个无参读口。
 * 声明可合并后并成一次（实测该页 2086ms 里有 1412ms 是这两条同一接口）。
 */
export const getI18nStats = (signal?: AbortSignal) =>
  gatewayRequest<ContentI18nStatsResp>(I18N_STATS, {
    method: 'POST',
    signal,
    dedupe: true,
  });

/** 页签登记及角标消费各归属方真实统计，缺数保留不可用。 */
export const getI18nLayers = async (
  signal?: AbortSignal,
): Promise<I18nLayerRegistration[]> => {
  const [stats, platform] = await Promise.all([
    getI18nStats(signal),
    gatewayRequest<{
      enabledLanguageCount: number | null;
      languageNames: string[];
      l2TermCount: number | null;
    }>('/admin/platform/i18n/sumPlatformI18nStats', { method: 'POST', signal }),
  ]);
  return [
    {
      layer: 'term',
      nameKey: 'sys.i18n.term.title',
      ownerKey: 'sys.i18n.owner.platform',
      permissionKey: 'sys.i18n.permission.readonly',
      total: platform.l2TermCount ?? undefined,
      tabSort: 3,
    },
    {
      layer: 'enum',
      nameKey: 'sys.i18n.layer.enum',
      ownerKey: 'sys.i18n.owner.platform',
      permissionKey: 'sys.i18n.permission.readonly',
      total: stats.enumGroupCount,
      tabSort: 2,
    },
    {
      layer: 'own',
      nameKey: 'sys.i18n.enum.footOwnLink',
      ownerKey: 'sys.i18n.owner.tenant',
      permissionKey: 'sys.i18n.permission.editable',
      total: stats.l3Total ?? undefined,
      unwiredCount: stats.unavailable.length,
      tabSort: 1,
    },
  ];
};

/** 读取选项：`silent` 给布局层用，失败时自己回退内置三语，不弹全局提示 */
export interface I18nLangsReadOptions {
  silent?: boolean;
}

/** 语言启用、主要/基准和本会默认分别取正式字段，缺覆盖率不制造百分比。 */
export const getI18nLangs = async (
  signal?: AbortSignal,
  options?: I18nLangsReadOptions,
): Promise<I18nLangsVO> => {
  // 两条接口都挂 sys.i18n.view：非超管进页必 403，这是常态而不是故障，
  // 布局层读语言策略要静默（10-09 §1.5 的固定文案由页面 403 统一给，不在这里弹）
  const skipErrorHandler = options?.silent === true;
  const [config, policy] = await Promise.all([
    gatewayRequest<{ rows: PlatformLanguageRowResp[] }>(LANGUAGES, {
      method: 'POST',
      data: {},
      signal,
      skipErrorHandler,
    }),
    gatewayRequest<LanguagePolicyResp>(
      '/admin/platform/i18n/readLanguagePolicy',
      { method: 'POST', signal, skipErrorHandler },
    ),
  ]);
  const rows: I18nLangVO[] = config.rows.map((row) => ({
    priority: row.sortNo,
    code: row.langCode,
    name: row.name,
    main: row.langCode === policy.primaryLang,
    base: row.langCode === policy.baselineLang,
    tenantDefault: row.langCode === policy.tenantDefaultLang,
    ends: row.ends,
    devEnds: row.devEnds,
    enabled: row.enabled,
    coverage: row.l3Coverage?.[row.langCode] ?? null,
    note: row.note,
  }));
  return {
    langs: rows.filter((row) => row.enabled !== false),
    disabledLangs: rows.filter((row) => row.enabled === false),
    mainLang: policy.primaryLang,
    baseLang: policy.baselineLang,
    defaultLang: policy.tenantDefaultLang,
    i18nEnabled: rows.filter((row) => row.enabled === true).length > 1,
    fallbackChain: [...new Set([policy.primaryLang, policy.baselineLang])],
  };
};

export const getI18nTerms = async (
  params: I18nTermQuery,
  signal?: AbortSignal,
): Promise<I18nTermListVO> => {
  const response = await gatewayRequest<PageIndustryTermsResp>(
    '/admin/platform/i18n/pageIndustryTerms',
    {
      method: 'POST',
      data: defined({
        kw: params.kw,
        page: params.page ?? 1,
        size: params.size ?? 20,
      }),
      signal,
    },
  );
  return {
    rows: response.rows.map((row) => ({
      termKey: row.key,
      baseName: row.baseValue,
      translations: row.values,
    })),
    total: response.total,
    page: response.page,
    size: response.size,
  };
};

export const getI18nL3 = async (
  params: I18nL3Query,
  signal?: AbortSignal,
): Promise<I18nL3VO> => {
  const data = await gatewayRequest<ContentOwnTranslationPageResp>(I18N_OWN, {
    method: 'POST',
    data: {
      ...defined({
        category: params.src,
        kw: params.kw,
        completeness:
          params.miss === undefined
            ? undefined
            : params.miss
            ? 'missing'
            : 'complete',
      }),
      page: { page: params.page ?? 1, size: params.size ?? 20 },
    },
    signal,
  });
  const rows: I18nL3RowVO[] = data.page.items.map((row) => ({
    source: row.category,
    sourceName: row.categoryName,
    entity: row.entity,
    entityId: row.entityId,
    baseName: row.baseline,
    presetName: row.presetName ?? undefined,
    translations: Object.fromEntries(
      Object.entries({
        'zh-HK': row.baseline,
        'zh-CN': row.zhCN,
        en: row.en,
      }).filter((entry): entry is [string, string] => entry[1] !== null),
    ),
    missingLangs: row.missingLangs,
    missingFieldsByLang: row.missingFieldsByLang,
    baselineFields: row.baselineFields,
    translationFields: row.translationFields,
    version: row.version,
    locked: !row.editable,
    maintainEntry: row.maintainEntry ?? undefined,
  }));
  return {
    rows,
    total: data.total,
    missing: data.incomplete,
    filteredTotal: data.page.total,
    page: data.page.page,
    size: data.page.size,
    srcOptions: data.categoryOptions.map((option) => ({
      key: option.code,
      label: option.name,
    })),
    sources: [],
    unwiredSources: data.unavailable.map((row) => ({
      source: row.category,
      reason: row.messageKey,
      owner: row.ownerService,
    })),
  };
};

/** 文件由后端对全部缺译记录生成并审计，前端只消费正式签名下载地址。 */
export const getI18nMissing = (signal?: AbortSignal) =>
  gatewayRequest<ContentExportMissingTranslationsResp>(
    '/admin/content/i18n/exportMissingTranslations',
    {
      method: 'POST',
      data: { format: 'xlsx' },
      signal,
      skipErrorHandler: true,
    },
  );

export const saveI18nFields = (
  row: I18nL3RowVO,
  fields: Record<string, Record<string, string>>,
  operationKey: string,
) =>
  gatewayRequest<ContentI18nTextSaveResp>(
    '/admin/content/i18n/saveMultilingualTexts',
    {
      method: 'POST',
      data: {
        entity: row.entity,
        entityId: row.entityId,
        fields,
        version: row.version,
        operationKey,
      },
    },
  );
